import type { LocalDb } from './db.ts';
import type { StockLevelRow, StockMovementRow } from './types.ts';

/** Movimientos que, solos, no activan el control de stock de un producto. */
const SALE_MOVEMENT_TYPES: readonly string[] = ['sale', 'sale_void', 'customer_return'];

export interface VariantStock {
  /** Stock en milésimas. */
  qty: number;
  /** Lleva control de stock: tuvo stock inicial, compras, ajustes o conteos (las ventas solas no cuentan). */
  tracked: boolean;
}

/**
 * Stock de cada variante en este dispositivo:
 *   resumen del servidor (stock_levels) + movimientos locales que ese resumen todavía no incluye.
 *
 * Un movimiento local está incluido en el resumen si el servidor ya lo aplicó (tiene
 * server_xid) y el resumen bajado es de esa transacción o posterior. Así, entre que se
 * envía un movimiento y se baja el resumen nuevo, no se cuenta dos veces ni se pierde.
 * Los movimientos rechazados por el servidor no cuentan.
 */
export function computeStock(
  levels: StockLevelRow[],
  movements: StockMovementRow[],
  rejectedMovementIds: ReadonlySet<string> = new Set(),
): Map<string, VariantStock> {
  const result = new Map<string, VariantStock>();
  const levelXid = new Map<string, bigint>();

  for (const level of levels) {
    const current = result.get(level.variant_id) ?? { qty: 0, tracked: false };
    current.qty += level.qty_milli;
    current.tracked ||= level.tracked ?? level.movements_count > 0;
    result.set(level.variant_id, current);
    const xid = BigInt(level.server_xid ?? '0');
    if (xid > (levelXid.get(level.variant_id) ?? -1n)) levelXid.set(level.variant_id, xid);
  }

  for (const m of movements) {
    if (rejectedMovementIds.has(m.id)) continue;
    const included = m.server_xid !== undefined && levelXid.has(m.variant_id) && levelXid.get(m.variant_id)! >= BigInt(m.server_xid);
    if (included) continue;
    const current = result.get(m.variant_id) ?? { qty: 0, tracked: false };
    current.qty += m.qty_milli;
    if (!SALE_MOVEMENT_TYPES.includes(m.type)) current.tracked = true;
    result.set(m.variant_id, current);
  }
  return result;
}

/** Ids de movimientos que el servidor rechazó (no deben contar en el stock). */
export async function rejectedMovementIds(db: LocalDb): Promise<Set<string>> {
  const ops = await db.outbox.where('[table+status]').anyOf([['stock_movements', 'rejected'], ['sales', 'rejected']]).toArray();
  return new Set(
    ops.flatMap((o) =>
      o.table === 'sales' ? ((o.payload.items as Array<{ movement_id: string }> | undefined) ?? []).map((i) => i.movement_id) : [o.row_id],
    ),
  );
}

/**
 * Borra movimientos locales que el resumen del servidor ya incluye y tienen más de
 * `days` días: el stock no cambia y la base local no crece sin límite.
 */
export async function pruneSettledMovements(db: LocalDb, days = 3, now = Date.now()): Promise<number> {
  const cutoff = new Date(now - days * 86_400_000).toISOString();
  return db.transaction('rw', [db.stock_movements, db.stock_levels], async () => {
    const old = await db.stock_movements.where('created_at').below(cutoff).toArray();
    if (old.length === 0) return 0;
    const levels = await db.stock_levels.where('variant_id').anyOf([...new Set(old.map((m) => m.variant_id))]).toArray();
    const xidByVariant = new Map<string, bigint>();
    for (const l of levels) {
      const x = BigInt(l.server_xid ?? '0');
      if (x > (xidByVariant.get(l.variant_id) ?? -1n)) xidByVariant.set(l.variant_id, x);
    }
    const settled = old
      .filter((m) => m.server_xid !== undefined && (xidByVariant.get(m.variant_id) ?? -1n) >= BigInt(m.server_xid))
      .map((m) => m.id);
    await db.stock_movements.bulkDelete(settled);
    return settled.length;
  });
}
